import { useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useParams } from "react-router-dom";
import { useAppContext } from "../app/AppContext";
import { ApiError } from "../core/api-client";
import {
  useCatalogFacets,
  useCatalogTemplate,
  useTemplateYaml,
} from "../features/templates/catalog-hooks";
import {
  facetLabels,
  TemplateFacetChips,
  TemplateKindLine,
  TemplateTeamSections,
} from "../features/templates/detail/TemplateDetailFacets";
import { InlineApiError, LoadingState } from "../shared/Shared";
import { YamlView } from "../shared/yaml/YamlView";
import styles from "./PublicPage.module.css";
import { SignInInPlace } from "./SignInInPlace";

/**
 * Signed-out template detail (PHASE1_PLAN.md stage 3.2/3.3): the prompt-free
 * public projection with a sign-in CTA. The signed-out LIST retired 2026-07-27
 * — /templates now renders the member gallery (TemplatesPage) in both trees,
 * so this detail page is the only public catalog surface left.
 *
 * The AGENT YAML is public too (Section 1 acceptance: a visitor reads the
 * template before signing up) — the same read-only pane the member detail
 * renders, in the same dark frame. No editor, no textbox, signed out or in.
 *
 * Like the member detail it shows the gallery card's labelled chips, says team
 * or single, lists a team's members or the teams a single sits in, and never
 * names the library (workflows/V5-Template-Organization.md Rev 2 §R2.2).
 */
export function PublicTemplateDetailPage() {
  const intl = useIntl();
  const { locale } = useAppContext();
  const params = useParams<{ libraryKey: string; slug: string }>();
  // The member detail's query key: a card opened from the gallery starts from its snapshot row.
  const detailQuery = useCatalogTemplate(params.libraryKey ?? "", params.slug ?? "", locale);
  const { facets } = useCatalogFacets(locale);
  const labels = useMemo(() => facetLabels(facets), [facets]);
  const template = detailQuery.data?.template;
  const yamlQuery = useTemplateYaml(template?.templateVersionId);
  const anonymousMiss = detailQuery.error instanceof ApiError && detailQuery.error.status === 404;
  return (
    <div data-library-key={params.libraryKey}>
      <Link className={styles.back} to="/templates">
        ← <FormattedMessage id="catalog.back" />
      </Link>
      {detailQuery.isLoading ? <LoadingState /> : null}
      <InlineApiError error={anonymousMiss ? null : detailQuery.error} />
      {template ? (
        <>
          <header className={styles.header}>
            <TemplateFacetChips
              template={template}
              labels={labels}
              style={{ marginBottom: "10px" }}
            />
            <h1 className={styles.title}>{template.name}</h1>
            <TemplateKindLine
              template={template}
              style={{ fontWeight: 700, fontSize: "13px", color: "var(--brand-muted)" }}
            />
            <p className={styles.sub}>{template.description}</p>
          </header>
          <TemplateTeamSections template={template} headingLevel={2} />
          <div
            style={{
              fontFamily: "var(--brand-font-display)",
              fontWeight: 700,
              fontSize: "11px",
              letterSpacing: "1px",
              color: "var(--brand-muted)",
              margin: "18px 0 8px",
              textTransform: "uppercase",
            }}
          >
            <FormattedMessage id="tpl.agentYaml" />
          </div>
          <div
            style={{
              background: "var(--brand-ink-deep)",
              border: "var(--brand-bw-2-5) solid var(--brand-ink)",
              borderRadius: "var(--brand-r-14)",
              overflow: "hidden",
            }}
          >
            {/* "…" while the YAML loads: the pane is never blank and never an editor. */}
            <YamlView
              value={yamlQuery.data?.yaml ?? "…"}
              ariaLabel={intl.formatMessage({ id: "tpl.agentYaml" })}
              height={260}
              dark
            />
          </div>
          <InlineApiError error={yamlQuery.error} />
          {/* Signing in swaps this CTA for the sign-in screen IN PLACE, so the
              exchange lands back on this exact template as the member detail. */}
          <SignInInPlace className={styles.cardCta} />
        </>
      ) : anonymousMiss ? (
        // A private owner detail is intentionally indistinguishable from a
        // nonexistent URL while signed out. Still offer authentication in
        // place so the same URL can resolve after session exchange.
        <SignInInPlace className={styles.cardCta} />
      ) : null}
    </div>
  );
}
